/**
 * Domain model. ESPN's shapes stop at the client boundary; everything above
 * this layer speaks in owners, weeks and margins.
 */
/**
 * A matchup's status, deriving it when absent. Season snapshots cached before
 * `status` existed come back without it; `isFinal` and the scores (both older
 * fields) are enough to recover it, so the scoreboard reads correctly from
 * those bundles without waiting for a resync.
 */
export function matchupStatus(m) {
    if (m.status)
        return m.status;
    if (m.isFinal)
        return 'final';
    return m.homeScore > 0 || m.awayScore > 0 ? 'live' : 'scheduled';
}
export function ownerById(data) {
    return new Map(data.owners.map((o) => [o.id, o]));
}
/**
 * Whether the season has kicked off: at least one game has started or finished.
 * `results` only ever carries started/final games (a scheduled 0-0 matchup is
 * kept in `matchups` but never becomes a result), so a non-empty results list
 * is the durable signal that we're past the pre-season. Both the nav (which
 * hides the War Room once games begin) and the API (which then freezes the
 * pre-season board) decide from this one function so they can't disagree.
 */
export function seasonHasStarted(data) {
    return data.results.length > 0;
}
/**
 * A view of the season as it stood through `week` — results, matchups and
 * completed-week flags all truncated to weeks <= week. Feeding this to
 * buildMoneyBoard yields the money picture "as of week N", which is what recaps
 * and share images must show: a week-9 recap should never reflect week-16 money.
 *
 * Only the fields buildMoneyBoard reads are truncated (results, matchups,
 * completedWeeks); teams are left as-is since the board doesn't consult them.
 */
export function seasonThroughWeek(data, week) {
    const results = data.results.filter((r) => r.week <= week);
    const matchups = data.matchups.filter((m) => m.week <= week);
    const weeksSeen = [...new Set(matchups.map((m) => m.week))].sort((a, b) => a - b);
    const completedWeeks = weeksSeen.filter((w) => matchups.filter((m) => m.week === w).every((m) => m.isFinal));
    return {
        ...data,
        results,
        matchups,
        completedWeeks,
        liveWeeks: weeksSeen.filter((w) => !completedWeeks.includes(w)),
    };
}
export function resultsFor(data, ownerId) {
    return data.results
        .filter((r) => r.ownerId === ownerId)
        .sort((a, b) => a.week - b.week);
}
/** Regular-season results only — every money award is regular season. */
export function regularSeason(data) {
    return data.results.filter((r) => !r.isPlayoff && r.week <= data.regularSeasonWeeks);
}
export function round1(n) {
    return Math.round(n * 10) / 10;
}
//# sourceMappingURL=model.js.map